import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  collection,
  doc,
  getCountFromServer,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  updatePassword,
} from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { auth, db, storage } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { resetPageTips } from "../services/pipService";
import { getReferralCode, getReferralLink, getReferralStats } from "../services/referralService";
import { useReferralOverlay } from "../context/ReferralOverlayContext";
import { fetchCreditSummary } from "../services/creditsService";
import { fetchCreatorDrops, fetchEarningsDoc } from "../services/creatorStudioService";
import { resolveNotificationGames } from "../constants/notificationGames";
import { computeMonthlyEarnings, formatMoneyWhole, nextPayoutDate } from "./creator/creatorHelpers";
import { formatCount } from "../utils/formatCount";
import {
  applyUsernameChange,
  centsToCad,
  createUsernameChangeSession,
  deactivateUserAccount,
  deleteUserAccount,
  fetchLoginEvents,
  formatBillingDate,
  formatConsentDate,
  formatLoginWhen,
  formatRenewDate,
  generateBackupRecoveryCodes,
  openBillingPortal,
  openCreatorStripeDashboard,
  refreshBillingFromStripe,
  requestUserDataExport,
  syncReferralBadgeState,
} from "../services/settingsService";
import {
  HubIcon,
  IconCheckCircle,
  IconClock,
  IconInfoCircle,
  IconInstagram,
  IconLock,
  IconShieldSmall,
  IconTikTok,
  ReferralBadgeIcon,
  SidebarIcon,
} from "./settingsIcons";
import "./Settings.css";

const BIO_MAX = 160;

const FAN_SECTIONS = [
  { id: "profile", label: "Profile", group: "Account" },
  { id: "account", label: "Account", group: "Account" },
  { id: "billing", label: "Billing", group: "Account", fanOnly: true },
  { id: "notifications", label: "Notifications", group: "Account" },
  { id: "privacy", label: "Privacy", group: "Account" },
  { id: "credits", label: "Credits & Pip", group: "Platform" },
  { id: "referral", label: "Referral", group: "Platform" },
  { id: "security", label: "Security", group: "Account safety" },
  { id: "data", label: "Data & privacy", group: "Account safety" },
];

const CREATOR_EXTRA = [
  { id: "creatortools", label: "Creator hub", group: "Creator tools", chevron: true },
  { id: "content", label: "Content settings", group: "Creator tools" },
  { id: "tax", label: "Tax information", group: "Creator tools" },
];

function Toggle({ on, onClick, label, disabled = false }) {
  return (
    <button
      type="button"
      className={`settings-toggle ${on ? "on" : ""}${disabled ? " is-disabled" : ""}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={on}
      aria-disabled={disabled}
    />
  );
}

function buildFormState(profile) {
  const p = profile || {};
  const games = resolveNotificationGames(p.notifications?.games);
  return {
    displayName: p.displayName || "",
    handle: p.handle || p.username || "",
    bio: p.bio || "",
    phone: p.settings?.phone || "",
    language: p.settings?.language || "English",
    timezone: p.settings?.timezone || "Eastern Time (ET)",
    pipName: p.pipName || "Pip",
    monthlySpendLimit: p.settings?.monthlySpendLimit ?? 100,
    researchOptIn: p.settings?.researchOptIn ?? true,
    pushEnabled: p.notifications?.pushEnabled ?? true,
    notifCreatorDrop: p.notifications?.creatorDrop ?? p.notifications?.drops ?? true,
    notifDropClosing: p.notifications?.dropClosingSoon ?? true,
    notifCredits: p.notifications?.creditsEarned ?? p.notifications?.credits ?? false,
    notifWall: p.notifications?.wallNote ?? p.notifications?.wall ?? true,
    notifFeatured: p.notifications?.featuredNomination ?? true,
    remindPushPrompt: p.notifications?.remindPushPrompt ?? true,
    gamesEnabled: games.enabled,
    gamesPlayed: games.played,
    gamesPassed: games.passed,
    gamesWeekly: games.weekly,
    gamesBox: games.box,
    emailFrequency: p.notifications?.emailFrequency || "Daily digest",
    emailWeeklyDigest: p.notifications?.weeklyDigest ?? true,
    emailProductUpdates: p.notifications?.productUpdates ?? false,
    emailPayoutConfirmation: p.notifications?.payoutConfirmation ?? true,
    showFirst100Badges: p.privacy?.showFirst100Badges ?? true,
    showFollowerCount: p.privacy?.showFollowerCount ?? true,
    showInSearch: p.privacy?.showInSearch ?? true,
    showPostingFrequency: p.privacy?.showPostingFrequency ?? true,
    defaultCommentAccess: p.privacy?.defaultCommentAccess || "Subscribers only",
    commentsDefault: p.settings?.commentsDefault ?? true,
    appreciationWallEnabled: p.settings?.appreciationWallEnabled ?? true,
    defaultDropPrice: p.settings?.defaultDropPrice ?? "9.00",
    defaultFollowerWindow: p.settings?.defaultFollowerWindow || "2 hours",
    contentLanguage: p.settings?.contentLanguage || "English",
    dropVisibility: p.settings?.dropVisibility || "Global — anyone can view",
    minimumPayoutThreshold: p.settings?.minimumPayoutThreshold || "$50 minimum",
    pipNotifyNearby: p.settings?.pipNotifyNearby ?? true,
    twoFactorEnabled: p.security?.twoFactorEnabled ?? false,
    emailOnNewDeviceLogin: p.security?.emailOnNewDeviceLogin ?? true,
    taxLegalName: p.tax?.legalName || "",
    taxSin: p.tax?.sinMasked || "",
    taxResidency: p.tax?.residency || "Canada",
    taxFilingType: p.tax?.filingType || "Individual (T4A)",
  };
}

function formToFirestorePatch(form, isCreator) {
  return {
    displayName: form.displayName.trim(),
    bio: form.bio.trim().slice(0, BIO_MAX),
    pipName: form.pipName.trim() || "Pip",
    settings: {
      phone: form.phone.trim(),
      language: form.language,
      timezone: form.timezone,
      monthlySpendLimit: Number(form.monthlySpendLimit) || 0,
      researchOptIn: form.researchOptIn,
      pipNotifyNearby: form.pipNotifyNearby,
      ...(isCreator
        ? {
            commentsDefault: form.commentsDefault,
            appreciationWallEnabled: form.appreciationWallEnabled,
            defaultDropPrice: form.defaultDropPrice,
            defaultFollowerWindow: form.defaultFollowerWindow,
            contentLanguage: form.contentLanguage,
            dropVisibility: form.dropVisibility,
            minimumPayoutThreshold: form.minimumPayoutThreshold,
          }
        : {}),
    },
    notifications: {
      pushEnabled: form.pushEnabled,
      creatorDrop: form.notifCreatorDrop,
      drops: form.notifCreatorDrop,
      dropClosingSoon: form.notifDropClosing,
      creditsEarned: form.notifCredits,
      credits: form.notifCredits,
      wallNote: form.notifWall,
      wall: form.notifWall,
      featuredNomination: form.notifFeatured,
      remindPushPrompt: form.remindPushPrompt,
      emailFrequency: form.emailFrequency,
      weeklyDigest: form.emailWeeklyDigest,
      productUpdates: form.emailProductUpdates,
      payoutConfirmation: form.emailPayoutConfirmation,
      games: resolveNotificationGames({
        enabled: form.gamesEnabled,
        played: form.gamesPlayed,
        passed: form.gamesPassed,
        weekly: form.gamesWeekly,
        box: form.gamesBox,
      }),
    },
    privacy: {
      showFirst100Badges: form.showFirst100Badges,
      showFollowerCount: form.showFollowerCount,
      showInSearch: form.showInSearch,
      showPostingFrequency: form.showPostingFrequency,
      defaultCommentAccess: form.defaultCommentAccess,
    },
    security: {
      twoFactorEnabled: form.twoFactorEnabled,
      emailOnNewDeviceLogin: form.emailOnNewDeviceLogin,
    },
    ...(isCreator
      ? {
          tax: {
            legalName: form.taxLegalName.trim(),
            sinMasked: form.taxSin,
            residency: form.taxResidency,
            filingType: form.taxFilingType,
            onFile: Boolean(form.taxLegalName),
          },
        }
      : {}),
    updatedAt: serverTimestamp(),
  };
}

export default function Settings() {
  const { currentUser, userProfile, userPerks, refreshUserProfile, refreshUserPerks, logout } = useAuth();
  const { openReferral } = useReferralOverlay();
  const [searchParams, setSearchParams] = useSearchParams();

  const isCreator = userProfile?.role === "creator" || userProfile?.isCreator;
  const sectionParam = searchParams.get("section") || "profile";

  const sections = useMemo(() => {
    const base = FAN_SECTIONS.filter((s) => !s.fanOnly || !isCreator);
    if (!isCreator) return base;
    const withCreator = [...base];
    const safetyIdx = withCreator.findIndex((s) => s.group === "Account safety");
    withCreator.splice(safetyIdx, 0, ...CREATOR_EXTRA);
    return withCreator;
  }, [isCreator]);

  const validIds = useMemo(() => sections.map((s) => s.id), [sections]);
  const activeSection = validIds.includes(sectionParam)
    ? sectionParam
    : validIds.includes("profile")
      ? "profile"
      : validIds[0];

  const [form, setForm] = useState(() => buildFormState(userProfile));
  const [savedForm, setSavedForm] = useState(() => buildFormState(userProfile));
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const avatarInputRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  const [usernamePayLoading, setUsernamePayLoading] = useState(false);
  const [recoveryCodesOpen, setRecoveryCodesOpen] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [billingRefreshing, setBillingRefreshing] = useState(false);
  const [referralBadges, setReferralBadges] = useState(null);

  const [creditBalance, setCreditBalance] = useState(0);
  const [referralCode, setReferralCode] = useState("");
  const [referralStats, setReferralStats] = useState({ total: 0, creditsEarned: 0, referrals: [] });
  const [loginEvents, setLoginEvents] = useState([]);
  const [creatorMeta, setCreatorMeta] = useState({ dropCount: 0, liveCount: 0, wallPending: 0 });
  const [monthEarnings, setMonthEarnings] = useState({ net: 0, payout: 0 });

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleLocked = userProfile?.handleLocked !== false && Boolean(userProfile?.handle || userProfile?.username);
  const handleUnlocked = Boolean(userProfile?.handleChangeUnlocked);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(savedForm) || Boolean(avatarFile),
    [form, savedForm, avatarFile]
  );

  useEffect(() => {
    if (!userProfile) return;
    const next = buildFormState(userProfile);
    setForm(next);
    setSavedForm(next);
    setAvatarPreview(userProfile.avatar || userProfile.avatarUrl || "");
  }, [userProfile]);

  useEffect(() => {
    if (searchParams.get("username") === "unlocked") {
      refreshUserProfile(currentUser?.uid);
    }
  }, [searchParams, currentUser?.uid, refreshUserProfile]);

  useEffect(() => {
    if (!currentUser?.uid) return;

    fetchCreditSummary(currentUser.uid, isCreator).then((s) => setCreditBalance(s.balance));

    getReferralCode(currentUser.uid).then(setReferralCode);
    getReferralStats(currentUser.uid).then(setReferralStats);
    fetchLoginEvents(currentUser.uid).then(setLoginEvents);

    if (isCreator) {
      Promise.all([
        fetchCreatorDrops(currentUser.uid),
        fetchEarningsDoc(currentUser.uid),
        getCountFromServer(
          query(
            collection(db, `appreciation_wall/${currentUser.uid}/notes`),
            where("status", "==", "pending")
          )
        ).catch(() => null),
      ]).then(([drops, earningsDoc, pendingSnap]) => {
        const live = drops.filter((d) => d.status === "open").length;
        setCreatorMeta({
          dropCount: drops.length,
          liveCount: live,
          wallPending: pendingSnap?.data?.()?.count ?? 0,
        });
        const monthStats = computeMonthlyEarnings(drops, userProfile);
        setMonthEarnings({
          net: earningsDoc?.monthToDate ?? monthStats.net,
          payout: earningsDoc?.nextPayoutAmount ?? monthStats.net * 0.9,
        });
      });
    }
  }, [currentUser?.uid, isCreator, userProfile]);

  // Live Stripe sync when opening Billing (fan) or Content/payouts (creator)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const needsBilling =
      activeSection === "billing"
      || (isCreator && (activeSection === "content" || activeSection === "tax"));
    if (!needsBilling) return;

    let cancelled = false;
    setBillingRefreshing(true);
    refreshBillingFromStripe()
      .then(() => {
        if (!cancelled) refreshUserProfile(currentUser.uid);
      })
      .catch((err) => {
        if (!cancelled) console.warn("Stripe billing refresh:", err.message);
      })
      .finally(() => {
        if (!cancelled) setBillingRefreshing(false);
      });

    return () => { cancelled = true; };
  }, [activeSection, currentUser?.uid, isCreator, refreshUserProfile]);

  // Hydrate from last server-written referralBadges on the profile
  useEffect(() => {
    if (userProfile?.referralBadges?.badges) {
      setReferralBadges(userProfile.referralBadges);
    }
  }, [userProfile?.uid, userProfile?.referralBadges]);

  // Recompute + persist on server when opening Referral (source of truth)
  useEffect(() => {
    if (!currentUser?.uid || !isCreator) return;
    if (activeSection !== "referral") return;

    let cancelled = false;
    syncReferralBadgeState()
      .then((data) => {
        if (cancelled) return;
        if (data?.referralBadges) setReferralBadges(data.referralBadges);
        return refreshUserProfile(currentUser.uid);
      })
      .catch((err) => {
        if (!cancelled) console.warn("Referral badge sync:", err.message);
      });

    return () => { cancelled = true; };
  }, [activeSection, currentUser?.uid, isCreator, refreshUserProfile]);

  const showToastMsg = useCallback((text) => {
    setToast(text);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const setSection = (id) => {
    setSearchParams({ section: id });
  };

  const patchForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const patchGameSub = (key, next) => {
    setForm((prev) => ({
      ...prev,
      [key]: next,
      // Turning any sub on while master is off re-enables the master.
      ...(next ? { gamesEnabled: true } : {}),
    }));
  };

  if (!currentUser) return <Navigate to="/welcome?auth=signin" replace />;

  const stripe = userProfile?.stripe || {};
  const pm = stripe.paymentMethod;
  const blocked = userProfile?.privacy?.blockedAccounts || [];
  const muted = userProfile?.privacy?.mutedAccounts || [];

  const handleSave = async () => {
    setSaving(true);
    try {
      let avatarUrl = avatarPreview;
      if (avatarFile) {
        const path = `avatars/${currentUser.uid}/${Date.now()}-${avatarFile.name.replace(/\s+/g, "-")}`;
        await uploadBytes(ref(storage, path), avatarFile);
        avatarUrl = await getDownloadURL(ref(storage, path));
      }

      const cleanedHandle = form.handle.trim().toLowerCase().replace(/^@/, "");
      if (handleUnlocked && cleanedHandle !== (savedForm.handle || "").replace(/^@/, "")) {
        await applyUsernameChange(cleanedHandle);
      }

      await updateDoc(doc(db, "users", currentUser.uid), {
        ...formToFirestorePatch(form, isCreator),
        avatar: avatarUrl,
        avatarUrl,
        ...(handleUnlocked ? {} : {}),
      });

      if (!userProfile?.handle && !handleLocked) {
        await updateDoc(doc(db, "users", currentUser.uid), {
          handle: cleanedHandle,
          username: cleanedHandle,
          handleLocked: true,
        });
      }

      await refreshUserProfile(currentUser.uid);
      setSavedForm({ ...form });
      setAvatarFile(null);
      showToastMsg("Settings saved.");
    } catch (err) {
      showToastMsg(err.message || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setForm(savedForm);
    setAvatarPreview(userProfile?.avatar || userProfile?.avatarUrl || "");
    setAvatarFile(null);
  };

  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await deactivateUserAccount();
      showToastMsg("Account deactivated. Sign in again anytime to reactivate.");
      if (typeof logout === "function") {
        await logout();
      } else {
        window.location.href = "/";
      }
    } catch (err) {
      showToastMsg(err.message || "Could not deactivate account.");
      setDeactivateLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.trim() !== "DELETE") {
      showToastMsg('Type DELETE to confirm.');
      return;
    }
    setDeleteLoading(true);
    try {
      await deleteUserAccount("DELETE");
      showToastMsg("Account deleted.");
      window.location.href = "/";
    } catch (err) {
      showToastMsg(err.message || "Could not delete account.");
      setDeleteLoading(false);
    }
  };

  const handleRequestExport = async () => {
    setExportLoading(true);
    try {
      const result = await requestUserDataExport();
      showToastMsg(
        result?.emailed
          ? "Export ready — check your email for the download link."
          : "Export ready."
      );
    } catch (err) {
      showToastMsg(err.message || "Could not start data export.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleUsernameCheckout = async () => {
    setUsernamePayLoading(true);
    try {
      const origin = window.location.origin;
      const { url } = await createUsernameChangeSession({
        successUrl: `${origin}/settings?section=profile&username=unlocked`,
        cancelUrl: `${origin}/settings?section=profile`,
      });
      if (url) window.location.href = url;
    } catch (err) {
      showToastMsg(err.message);
    } finally {
      setUsernamePayLoading(false);
      setUsernameModalOpen(false);
    }
  };

  const handleGenerateCodes = async () => {
    setRecoveryLoading(true);
    try {
      const { codes } = await generateBackupRecoveryCodes();
      setRecoveryCodes(codes || []);
      setRecoveryCodesOpen(true);
    } catch (err) {
      showToastMsg(err.message);
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!currentPassword || !newPassword) {
      showToastMsg("Enter current and new password.");
      return;
    }
    setPasswordSaving(true);
    try {
      const cred = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      showToastMsg("Password updated.");
    } catch {
      showToastMsg("Could not update password. Check your current password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleVerifyEmail = async () => {
    try {
      await sendEmailVerification(currentUser);
      showToastMsg("Verification email sent.");
    } catch {
      showToastMsg("Could not send verification email.");
    }
  };

  const handleUnblock = async (handle) => {
    const next = blocked.filter((h) => h !== handle);
    await updateDoc(doc(db, "users", currentUser.uid), {
      "privacy.blockedAccounts": next,
      updatedAt: serverTimestamp(),
    });
    await refreshUserProfile(currentUser.uid);
  };

  const handleUnmute = async (handle) => {
    const next = muted.filter((h) => h !== handle);
    await updateDoc(doc(db, "users", currentUser.uid), {
      "privacy.mutedAccounts": next,
      updatedAt: serverTimestamp(),
    });
    await refreshUserProfile(currentUser.uid);
  };

  const referralLink = referralCode ? getReferralLink(referralCode) : "";
  const unlockedReferrals = referralStats.referrals?.filter((r) => r.status === "completed").length ?? referralStats.total;

  const consentDate = formatConsentDate(userProfile?.consentedAt);
  const consentVersion = userProfile?.consentedTermsVersion || "1.0";
  const ageDate = formatConsentDate(userProfile?.ageConfirmedAt || userProfile?.createdAt);

  const groupedSections = sections.reduce((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  return (
    <div className="settings-page">
      <div className="settings-page-layout">
        <aside className="settings-sidebar">
          {Object.entries(groupedSections).map(([group, items]) => (
            <div key={group}>
              <div className="settings-sb-label">{group}</div>
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`settings-sb-item ${item.chevron ? "chevron" : ""} ${activeSection === item.id ? "active" : ""}`}
                  onClick={() => setSection(item.id)}
                >
                  <SidebarIcon id={item.id} />
                  {item.label}
                </button>
              ))}
            </div>
          ))}

          {isCreator && (
            <div className="settings-sb-earnings">
              <div className="settings-se-label">This month</div>
              <div className="settings-se-amount">{formatMoneyWhole(monthEarnings.net)}</div>
              <div className="settings-se-sub">
                {formatMoneyWhole(monthEarnings.payout)} payout {nextPayoutDate()}
              </div>
              <span className="settings-se-hand">after platform fee</span>
            </div>
          )}
        </aside>

        <main className="settings-main-col">
          {/* PROFILE */}
          <div className={`settings-section-panel ${activeSection === "profile" ? "active" : ""}`}>
            <h1 className="settings-section-title">Profile</h1>
            <span className="settings-section-hand">how you show up across the platform</span>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Identity</div>
                  <div className="settings-card-sub">Your name and handle</div>
                </div>
              </div>
              <div className="settings-field-row">
                <div className="settings-field">
                  <span className="settings-field-label">Display Name</span>
                  <input className="settings-field-input" value={form.displayName} onChange={(e) => patchForm("displayName", e.target.value)} />
                </div>
                <div className="settings-field">
                  <span className="settings-field-label">Username</span>
                  <div className="settings-locked-row">
                    <input
                      className="settings-field-input"
                      value={form.handle.startsWith("@") ? form.handle : `@${form.handle}`}
                      disabled={handleLocked && !handleUnlocked}
                      onChange={(e) => patchForm("handle", e.target.value.replace(/^@/, ""))}
                    />
                    {handleLocked && !handleUnlocked && (
                      <button type="button" className="settings-locked-link" onClick={() => setUsernameModalOpen(true)}>
                        <IconLock />
                        $7.99 · change
                      </button>
                    )}
                  </div>
                  <span className="settings-field-hint">
                    {handleUnlocked
                      ? "Unlocked — save once to apply your new username. Old handle redirects 30 days, then held 90 days."
                      : "Locked once set — one paid change ever ($7.99). Old handle redirects 30 days, then 90-day hold."}
                  </span>
                </div>
              </div>
              <div className="settings-field-row single">
                <div className="settings-field">
                  <span className="settings-field-label">Bio</span>
                  <textarea className="settings-field-input" rows={3} value={form.bio} maxLength={BIO_MAX} onChange={(e) => patchForm("bio", e.target.value)} />
                  <span className="settings-field-hint">{BIO_MAX} characters max</span>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Profile Photo</div></div>
              <div className="settings-avatar-row">
                <img className="settings-avatar-img" src={avatarPreview || "/default-avatar.png"} alt="" />
                <div className="settings-avatar-actions">
                  <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setAvatarFile(file);
                    setAvatarPreview(URL.createObjectURL(file));
                  }} />
                  <button type="button" className="settings-btn settings-btn-primary" onClick={() => avatarInputRef.current?.click()}>Upload New</button>
                  <button type="button" className="settings-btn settings-btn-ghost" onClick={() => { setAvatarFile(null); setAvatarPreview(userProfile?.avatar || ""); }}>Remove</button>
                </div>
              </div>
            </div>
          </div>

          {/* ACCOUNT */}
          <div className={`settings-section-panel ${activeSection === "account" ? "active" : ""}`}>
            <h1 className="settings-section-title">Account</h1>
            <span className="settings-section-hand">login and contact details</span>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Contact</div>
                  <div className="settings-card-sub">Changing your email sends a confirmation link before it takes effect</div>
                </div>
              </div>
              <div className="settings-field-row">
                <div className="settings-field">
                  <span className="settings-field-label">Email</span>
                  <input className="settings-field-input" value={currentUser.email || ""} disabled readOnly />
                  <span className={`settings-field-hint ${currentUser.emailVerified ? "good" : ""}`}>
                    {currentUser.emailVerified ? "✓ Verified" : (
                      <button type="button" className="settings-btn settings-btn-ghost" style={{ marginTop: 6, fontSize: 11, padding: "5px 12px" }} onClick={handleVerifyEmail}>Send verification</button>
                    )}
                  </span>
                </div>
                <div className="settings-field">
                  <span className="settings-field-label">Phone (optional)</span>
                  <input className="settings-field-input" placeholder="For account recovery only" value={form.phone} onChange={(e) => patchForm("phone", e.target.value)} />
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Connected Accounts</div>
                  <div className="settings-card-sub">{isCreator ? "Verified links help fans know you're really you" : "Optional — link accounts to personalize recommendations"}</div>
                </div>
              </div>
              {[
                { platform: "Instagram", Icon: IconInstagram },
                { platform: "TikTok", Icon: IconTikTok },
              ].map(({ platform, Icon }) => (
                <div key={platform} className="settings-connect-row">
                  <div className="settings-connect-icon">
                    <Icon />
                  </div>
                  <div className="settings-connect-body">
                    <div className="settings-connect-title">{platform}</div>
                    <div className="settings-connect-sub">Not connected</div>
                  </div>
                  <button type="button" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "6px 14px" }} disabled>Connect</button>
                </div>
              ))}
            </div>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Language &amp; Region</div></div>
              <div className="settings-field-row">
                <div className="settings-field">
                  <span className="settings-field-label">Language</span>
                  <select className="settings-field-input" value={form.language} onChange={(e) => patchForm("language", e.target.value)}>
                    <option>English</option>
                    <option>Français</option>
                  </select>
                </div>
                <div className="settings-field">
                  <span className="settings-field-label">Time Zone</span>
                  <select className="settings-field-input" value={form.timezone} onChange={(e) => patchForm("timezone", e.target.value)}>
                    <option>Eastern Time (ET)</option>
                    <option>Pacific Time (PT)</option>
                    <option>Mountain Time (MT)</option>
                    <option>Central Time (CT)</option>
                  </select>
                </div>
              </div>
            </div>

            {!isCreator && (
              <div className="settings-card">
                <div className="settings-card-head"><div className="settings-card-title">Age Verification</div></div>
                <div className="settings-age-strip">
                  <IconCheckCircle />
                  You confirmed you&apos;re 18 or older when you signed up{ageDate ? ` on ${ageDate}` : ""}. 100ViewsOnly is not intended for minors — contact support if this needs correcting.
                </div>
              </div>
            )}

            <div className="settings-card settings-danger-zone">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Danger Zone</div>
                  <div className="settings-card-sub" style={{ color: "var(--st-red)", opacity: 0.75 }}>These actions are permanent</div>
                </div>
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Deactivate account</div>
                  <div className="settings-tr-desc">Hide your profile temporarily. You can reactivate anytime by logging back in.</div>
                </div>
                <button
                  type="button"
                  className="settings-btn settings-btn-ghost"
                  disabled={deactivateLoading}
                  onClick={handleDeactivate}
                >
                  {deactivateLoading ? "Working…" : "Deactivate"}
                </button>
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Delete account</div>
                  <div className="settings-tr-desc">Permanently erase your account and data. This cannot be undone.</div>
                </div>
                <button
                  type="button"
                  className="settings-btn settings-btn-danger"
                  onClick={() => { setDeleteConfirmText(""); setDeleteModalOpen(true); }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          {/* BILLING — fan only */}
          {!isCreator && (
            <div className={`settings-section-panel ${activeSection === "billing" ? "active" : ""}`}>
              <h1 className="settings-section-title">Billing</h1>
              <span className="settings-section-hand">payment methods and active subscriptions</span>
              {billingRefreshing && (
                <p className="settings-empty-note" style={{ marginBottom: 12 }}>Refreshing from Stripe…</p>
              )}

              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Payment Method</div>
                    <div className="settings-card-sub">Used for drop unlocks, tips, and subscriptions</div>
                  </div>
                </div>
                <div className="settings-pm-card">
                  <div className="settings-pm-icon">{pm?.brand || "CARD"}</div>
                  <div className="settings-pm-body">
                    <div className="settings-pm-title">{pm ? `•••• •••• •••• ${pm.last4}` : "No card on file"}</div>
                    <div className="settings-pm-sub">{pm?.expMonth ? `Expires ${String(pm.expMonth).padStart(2, "0")}/${String(pm.expYear).slice(-2)}` : "Add a card in Stripe"}</div>
                  </div>
                  <button type="button" className="settings-stripe-btn" onClick={() => openBillingPortal({ returnUrl: `${window.location.origin}/settings?section=billing` }).catch((e) => showToastMsg(e.message))}>
                    Manage in Stripe ↗
                  </button>
                </div>
                <div className="settings-stripe-note">
                  <IconShieldSmall />
                  Card details are stored and secured by Stripe — 100ViewsOnly never sees or stores your full card number
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Active Subscriptions</div>
                    <div className="settings-card-sub">{(stripe.subscriptions || []).length} creators · billed monthly</div>
                  </div>
                </div>
                {(stripe.subscriptions || []).length === 0 ? (
                  <p className="settings-empty-note">No active subscriptions</p>
                ) : (
                  (stripe.subscriptions || []).map((sub) => (
                    <div key={sub.subscriptionId} className="settings-sub-row">
                      <img className="settings-sub-av" src={sub.avatarUrl || "/default-avatar.png"} alt="" />
                      <div className="settings-sub-body">
                        <div className="settings-sub-name">{sub.creatorName} · {sub.tierName}</div>
                        <div className="settings-sub-meta">
                          {centsToCad(sub.amountCents)}/mo · renews {formatRenewDate(sub.renewAt)}
                          {sub.foundingRate ? " · founding rate locked" : ""}
                        </div>
                      </div>
                      <button type="button" className="settings-stripe-btn" onClick={() => openBillingPortal().catch((e) => showToastMsg(e.message))}>Manage ↗</button>
                    </div>
                  ))
                )}
                <div className="settings-stripe-note">
                  <IconInfoCircle />
                  Cancelling takes effect at the end of your current billing period — you keep access until then
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-head"><div className="settings-card-title">Billing History</div></div>
                {(stripe.billingHistory || []).length === 0 ? (
                  <p className="settings-empty-note">No billing history yet</p>
                ) : (
                  (stripe.billingHistory || []).map((row) => (
                    <div key={row.id} className="settings-invoice-row">
                      <div className="settings-invoice-meta">
                        {row.label}
                        <span>{formatBillingDate(row.date)} · {centsToCad(row.amountCents)}</span>
                      </div>
                      {row.receiptUrl ? (
                        <a href={row.receiptUrl} target="_blank" rel="noopener noreferrer" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }}>Receipt</a>
                      ) : (
                        <button type="button" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }} disabled>Receipt</button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* NOTIFICATIONS */}
          <div className={`settings-section-panel ${activeSection === "notifications" ? "active" : ""}`}>
            <h1 className="settings-section-title">Notifications</h1>
            <span className="settings-section-hand">what you hear about, and how</span>
            <p className="settings-games-summary">
              { [form.gamesPlayed, form.gamesPassed, form.gamesWeekly, form.gamesBox].filter(Boolean).length } of 4 game notifications on.
            </p>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Push Notifications</div></div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">All push notifications <span className="settings-mini-badge settings-mb-gold">Master switch</span></div>
                  <div className="settings-tr-desc">Turn everything below off in one tap</div>
                </div>
                <Toggle on={form.pushEnabled} onClick={() => patchForm("pushEnabled", !form.pushEnabled)} label="All push notifications" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Remind me to enable notifications</div>
                  <div className="settings-tr-desc">Show the permission bar again after a cooldown if you haven&apos;t allowed push yet</div>
                </div>
                <Toggle on={form.remindPushPrompt} onClick={() => patchForm("remindPushPrompt", !form.remindPushPrompt)} label="Remind me to enable notifications" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Followed creator posts a drop</div>
                  <div className="settings-tr-desc">The moment a window opens</div>
                </div>
                <Toggle on={form.notifCreatorDrop} onClick={() => patchForm("notifCreatorDrop", !form.notifCreatorDrop)} label="Creator drop notifications" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Drop closing soon</div>
                  <div className="settings-tr-desc">Under 10 spots left on a drop you&apos;re watching</div>
                </div>
                <Toggle on={form.notifDropClosing} onClick={() => patchForm("notifDropClosing", !form.notifDropClosing)} label="Drop closing soon" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Credits earned</div>
                  <div className="settings-tr-desc">First view bonus, referral rewards, Pip finds</div>
                </div>
                <Toggle on={form.notifCredits} onClick={() => patchForm("notifCredits", !form.notifCredits)} label="Credits earned" />
              </div>
              {isCreator && (
                <>
                  <div className="settings-toggle-row">
                    <div className="settings-tr-info">
                      <div className="settings-tr-title">New Appreciation Wall note</div>
                      <div className="settings-tr-desc">A fan sent you a note to review</div>
                    </div>
                    <Toggle on={form.notifWall} onClick={() => patchForm("notifWall", !form.notifWall)} label="Wall note notifications" />
                  </div>
                  <div className="settings-toggle-row">
                    <div className="settings-tr-info">
                      <div className="settings-tr-title">Featured Creator nomination</div>
                      <div className="settings-tr-desc">You&apos;ve been picked for the Home or Gallery spotlight</div>
                    </div>
                    <Toggle on={form.notifFeatured} onClick={() => patchForm("notifFeatured", !form.notifFeatured)} label="Featured nomination" />
                  </div>
                </>
              )}
            </div>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Games</div></div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Game notifications <span className="settings-mini-badge settings-mb-gold">Master switch</span></div>
                  <div className="settings-tr-desc">Master switch for everything below</div>
                </div>
                <Toggle
                  on={form.gamesEnabled}
                  onClick={() => patchForm("gamesEnabled", !form.gamesEnabled)}
                  label="Game notifications"
                />
              </div>
              {[
                {
                  key: "gamesPlayed",
                  on: form.gamesPlayed,
                  title: "A creator played Word Drop or Number Drop",
                  desc: "One a day per creator, only ones you follow",
                  label: "Creator played Word Drop or Number Drop",
                },
                {
                  key: "gamesPassed",
                  on: form.gamesPassed,
                  title: "Someone passed you",
                  desc: "Default OFF",
                  label: "Someone passed you",
                },
                {
                  key: "gamesWeekly",
                  on: form.gamesWeekly,
                  title: "Weekly results",
                  desc: "Monday morning",
                  label: "Weekly results",
                },
                {
                  key: "gamesBox",
                  on: form.gamesBox,
                  title: "Pip's daily box",
                  desc: "Evening nudge if unopened",
                  label: "Pip's daily box",
                },
              ].map((row) => {
                const masterOff = !form.gamesEnabled;
                // Master-off: dim all four; already-on rows are locked; off rows can be turned on (re-enables master).
                const lockedOn = masterOff && row.on;
                return (
                  <div
                    key={row.key}
                    className={`settings-toggle-row${masterOff ? " is-dimmed" : ""}`}
                  >
                    <div className="settings-tr-info">
                      <div className="settings-tr-title">{row.title}</div>
                      <div className="settings-tr-desc">{row.desc}</div>
                    </div>
                    <Toggle
                      on={row.on}
                      disabled={lockedOn}
                      onClick={() => {
                        if (masterOff) {
                          if (!row.on) patchGameSub(row.key, true);
                          return;
                        }
                        patchGameSub(row.key, !row.on);
                      }}
                      label={row.label}
                    />
                  </div>
                );
              })}
            </div>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Email Notifications</div>
                  <div className="settings-card-sub">Separate from push — some people want email only</div>
                </div>
              </div>
              <div className="settings-field-row single" style={{ marginBottom: 16 }}>
                <div className="settings-field">
                  <span className="settings-field-label">How often</span>
                  <select className="settings-field-input" value={form.emailFrequency} onChange={(e) => patchForm("emailFrequency", e.target.value)}>
                    <option>Immediately</option>
                    <option>Daily digest</option>
                    <option>Weekly digest</option>
                  </select>
                  <span className="settings-field-hint">Applies to drop and activity emails — not account/security emails</span>
                </div>
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Weekly digest</div>
                  <div className="settings-tr-desc">A quiet Sunday summary of what you missed</div>
                </div>
                <Toggle on={form.emailWeeklyDigest} onClick={() => patchForm("emailWeeklyDigest", !form.emailWeeklyDigest)} label="Weekly digest" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Product updates</div>
                  <div className="settings-tr-desc">New features and platform announcements</div>
                </div>
                <Toggle on={form.emailProductUpdates} onClick={() => patchForm("emailProductUpdates", !form.emailProductUpdates)} label="Product updates" />
              </div>
              {isCreator && (
                <div className="settings-toggle-row">
                  <div className="settings-tr-info">
                    <div className="settings-tr-title">Monthly payout confirmation</div>
                    <div className="settings-tr-desc">Sent every time a payout lands</div>
                  </div>
                  <Toggle on={form.emailPayoutConfirmation} onClick={() => patchForm("emailPayoutConfirmation", !form.emailPayoutConfirmation)} label="Payout confirmation" />
                </div>
              )}
            </div>
          </div>

          {/* PRIVACY */}
          <div className={`settings-section-panel ${activeSection === "privacy" ? "active" : ""}`}>
            <h1 className="settings-section-title">Privacy</h1>
            <span className="settings-section-hand">who sees what</span>

            <div className="settings-card">
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Show my First 100 badges publicly</div>
                  <div className="settings-tr-desc">Rank badges appear on comments and your Trophy Case</div>
                </div>
                <Toggle on={form.showFirst100Badges} onClick={() => patchForm("showFirst100Badges", !form.showFirst100Badges)} label="Show First 100 badges" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info"><div className="settings-tr-title">Show follower count on my profile</div></div>
                <Toggle on={form.showFollowerCount} onClick={() => patchForm("showFollowerCount", !form.showFollowerCount)} label="Show follower count" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Show me in search results</div>
                  <div className="settings-tr-desc">Turning this off still lets people you follow find you directly</div>
                </div>
                <Toggle on={form.showInSearch} onClick={() => patchForm("showInSearch", !form.showInSearch)} label="Show in search" />
              </div>
              {isCreator && (
                <div className="settings-toggle-row">
                  <div className="settings-tr-info">
                    <div className="settings-tr-title">Show posting frequency on profile</div>
                    <div className="settings-tr-desc">&quot;Typically posts weekly&quot; line under your bio</div>
                  </div>
                  <Toggle on={form.showPostingFrequency} onClick={() => patchForm("showPostingFrequency", !form.showPostingFrequency)} label="Show posting frequency" />
                </div>
              )}
            </div>

            {isCreator && (
              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Who Can Comment</div>
                    <div className="settings-card-sub">Applies as the default across your drops</div>
                  </div>
                </div>
                <div className="settings-field-row single">
                  <div className="settings-field">
                    <select className="settings-field-input" value={form.defaultCommentAccess} onChange={(e) => patchForm("defaultCommentAccess", e.target.value)}>
                      <option>Everyone</option>
                      <option>Subscribers only</option>
                      <option>Nobody — comments off</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Blocked Accounts</div>
                  <div className="settings-card-sub">{blocked.length} people blocked</div>
                </div>
              </div>
              {blocked.length === 0 ? (
                <p className="settings-empty-note">No blocked accounts</p>
              ) : (
                blocked.map((handle) => (
                  <div key={handle} className="settings-list-row">
                    <span className="settings-list-label">@{handle}</span>
                    <button type="button" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => handleUnblock(handle)}>Unblock</button>
                  </div>
                ))
              )}
            </div>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Muted Accounts</div>
                  <div className="settings-card-sub">Softer than blocking — they can&apos;t tell they&apos;re muted</div>
                </div>
              </div>
              {muted.length === 0 ? (
                <p className="settings-empty-note">No muted accounts</p>
              ) : (
                muted.map((handle) => (
                  <div key={handle} className="settings-list-row">
                    <span className="settings-list-label">@{handle}</span>
                    <button type="button" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => handleUnmute(handle)}>Unmute</button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* CREDITS & PIP */}
          <div className={`settings-section-panel ${activeSection === "credits" ? "active" : ""}`}>
            <h1 className="settings-section-title">Credits &amp; <em>Pip</em></h1>
            <span className="settings-section-hand">your balance and Pip preferences</span>

            <div className="settings-credit-strip">
              <div>
                <div className="settings-cs-amount">{formatCount(creditBalance)}</div>
                <div className="settings-cs-label">Credit balance</div>
              </div>
              <Link to="/perks" className="settings-cs-link">View transaction history →</Link>
            </div>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Pip</div>
                  <div className="settings-card-sub">Your easter-egg hunting companion</div>
                </div>
              </div>
              <div className="settings-field-row single">
                <div className="settings-field">
                  <span className="settings-field-label">Pip&apos;s name</span>
                  <input className="settings-field-input" value={form.pipName} readOnly disabled />
                  <span className="settings-field-hint">Rename for 250 credits, in Perks</span>
                </div>
              </div>
              <div className="settings-toggle-row" style={{ marginTop: 6 }}>
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Notify me when Pip is nearby</div>
                  <div className="settings-tr-desc">A subtle toast hint on pages where Pip has spawned</div>
                </div>
                <Toggle on={form.pipNotifyNearby} onClick={() => patchForm("pipNotifyNearby", !form.pipNotifyNearby)} label="Pip nearby notifications" />
              </div>
              <div className="settings-toggle-row" style={{ marginTop: 6 }}>
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Show page tips again</div>
                  <div className="settings-tr-desc">Replay the Follow, Credits, and Following tips the next time you visit those pages</div>
                </div>
                <Toggle
                  on={Boolean(
                    userPerks &&
                    !userPerks.seenFollowTip &&
                    !userPerks.seenCreditsTip &&
                    !userPerks.seenCollectionTip
                  )}
                  onClick={async () => {
                    try {
                      await resetPageTips();
                      if (refreshUserPerks && currentUser) await refreshUserPerks(currentUser.uid);
                      showToastMsg("Page tips will show again on their pages");
                    } catch (err) {
                      showToastMsg(err.message || "Could not reset page tips");
                    }
                  }}
                  label="Show page tips again"
                />
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Monthly Reset</div></div>
              <div style={{ fontSize: 12, color: "var(--st-text-mid)", lineHeight: 1.7 }}>
                Credits reset on the 1st of each month at 2am ET. You carry over <strong>10%</strong> of your balance as loyalty credits — the rest resets to keep the economy fair for everyone.
              </div>
            </div>

            {!isCreator && (
              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Monthly Spending Limit</div>
                    <div className="settings-card-sub">Optional — a cap on real-money spend across drop unlocks and tips</div>
                  </div>
                </div>
                <div className="settings-slider-row">
                  <input
                    type="range"
                    min={0}
                    max={500}
                    step={10}
                    value={form.monthlySpendLimit}
                    onChange={(e) => patchForm("monthlySpendLimit", Number(e.target.value))}
                  />
                  <span className="settings-slider-val">
                    ${form.monthlySpendLimit}{form.monthlySpendLimit >= 500 ? "+" : ""}
                  </span>
                </div>
                <span className="settings-field-hint" style={{ display: "block", marginTop: 8 }}>
                  Set to $0 for no spending this month. You&apos;ll get a warning at 80% of your limit.
                </span>
              </div>
            )}
          </div>

          {/* REFERRAL */}
          <div className={`settings-section-panel ${activeSection === "referral" ? "active" : ""}`}>
            <h1 className="settings-section-title">Referral</h1>
            <span className="settings-section-hand">bring a friend, you both earn</span>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Referral tools</div>
                  <div className="settings-card-sub">Copy your link, share it, and see your badge ladder</div>
                </div>
              </div>
              <button type="button" className="settings-cs-link" onClick={openReferral}>
                Open referral tools →
              </button>
            </div>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Your Referral Link</div>
                  <div className="settings-card-sub">Both of you get +50 credits after their first unlock</div>
                </div>
              </div>
              <div className="settings-referral-box">
                <span className="settings-referral-link">{referralLink || "Generating link…"}</span>
                <button
                  type="button"
                  className="settings-referral-copy"
                  onClick={() => {
                    if (!referralLink) return;
                    navigator.clipboard.writeText(referralLink);
                    showToastMsg("Copied!");
                  }}
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Referral Activity</div></div>
              <div className="settings-stat-row">
                <div className="settings-stat-box">
                  <div className="settings-stat-num">{referralStats.total}</div>
                  <div className="settings-stat-lbl">Joined</div>
                </div>
                <div className="settings-stat-box">
                  <div className="settings-stat-num">{unlockedReferrals}</div>
                  <div className="settings-stat-lbl">Unlocked</div>
                </div>
                <div className="settings-stat-box">
                  <div className="settings-stat-num">{referralStats.creditsEarned}</div>
                  <div className="settings-stat-lbl">Credits earned</div>
                </div>
              </div>
            </div>

            {isCreator && (
              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Creator Referral Badges</div>
                    <div className="settings-card-sub">
                      {(referralBadges?.count ?? userProfile?.lifetimeCreatorReferrals ?? 0)} of 100
                      {referralBadges?.nextThreshold
                        ? ` — next badge at ${referralBadges.nextThreshold} approved creator referrals`
                        : referralBadges?.count >= 100
                          ? " — ladder complete"
                          : " — unlocked when a referred creator fills their first drop"}
                    </div>
                  </div>
                </div>
                <div className="settings-badge-ladder">
                  {(referralBadges?.badges || []).map((badge) => (
                    <div
                      key={badge.id}
                      className={`settings-badge-item${badge.state === "earned" ? " earned" : ""}${badge.state === "current" ? " current" : ""}`}
                      title={badge.extra || `${badge.label} · ${badge.threshold}`}
                    >
                      <div className="settings-badge-icon">
                        <ReferralBadgeIcon name={badge.icon} />
                      </div>
                      <div className="settings-badge-name">{badge.label}</div>
                      <div className="settings-badge-thresh">{badge.threshLabel}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CREATOR HUB */}
          {isCreator && (
            <div className={`settings-section-panel ${activeSection === "creatortools" ? "active" : ""}`}>
              <h1 className="settings-section-title">Creator <em>Hub</em></h1>
              <span className="settings-section-hand">jump straight to your tools</span>
              <div className="settings-card">
                {[
                  { to: "/dashboard", title: "Dashboard", sub: "Overview, stats, recent activity" },
                  { to: "/drops", title: "Manage Drops", sub: `${creatorMeta.dropCount} total · ${creatorMeta.liveCount} live` },
                  { to: "/earnings", title: "Earnings", sub: "Payout history and breakdown" },
                  { to: "/analytics", title: "Analytics", sub: "Growth and engagement trends" },
                  { to: "/wall", title: "Appreciation Wall", sub: `${creatorMeta.wallPending} notes pending review` },
                ].map((row) => (
                  <Link key={row.to} to={row.to} className="settings-hub-row">
                    <div className="settings-hub-icon">
                      <HubIcon to={row.to} />
                    </div>
                    <div className="settings-hub-body">
                      <div className="settings-hub-title">{row.title}</div>
                      <div className="settings-hub-sub">{row.sub}</div>
                    </div>
                    <div className="settings-hub-chev" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CONTENT SETTINGS */}
          {isCreator && (
            <div className={`settings-section-panel ${activeSection === "content" ? "active" : ""}`}>
              <h1 className="settings-section-title">Content <em>Settings</em></h1>
              <span className="settings-section-hand">defaults applied to every new drop</span>

              <div className="settings-card">
                <div className="settings-toggle-row">
                  <div className="settings-tr-info">
                    <div className="settings-tr-title">Allow comments by default</div>
                    <div className="settings-tr-desc">You can still turn this off per drop</div>
                  </div>
                  <Toggle on={form.commentsDefault} onClick={() => patchForm("commentsDefault", !form.commentsDefault)} label="Comments default" />
                </div>
                <div className="settings-toggle-row">
                  <div className="settings-tr-info">
                    <div className="settings-tr-title">Appreciation Wall enabled</div>
                    <div className="settings-tr-desc">Turning this off hides your wall from your profile entirely</div>
                  </div>
                  <Toggle on={form.appreciationWallEnabled} onClick={() => patchForm("appreciationWallEnabled", !form.appreciationWallEnabled)} label="Appreciation wall" />
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Default Drop Settings</div>
                    <div className="settings-card-sub">Pre-filled on Post a Drop — change anytime per drop</div>
                  </div>
                </div>
                <div className="settings-field-row">
                  <div className="settings-field">
                    <span className="settings-field-label">Default Price (CAD)</span>
                    <input className="settings-field-input" value={`$${form.defaultDropPrice}`} onChange={(e) => patchForm("defaultDropPrice", e.target.value.replace(/[^0-9.]/g, ""))} />
                    <span className="settings-field-hint">Recommended range: $5–$10</span>
                  </div>
                  <div className="settings-field">
                    <span className="settings-field-label">Default Follower Window</span>
                    <select className="settings-field-input" value={form.defaultFollowerWindow} onChange={(e) => patchForm("defaultFollowerWindow", e.target.value)}>
                      <option>1 hour</option>
                      <option>2 hours</option>
                      <option>4 hours</option>
                      <option>8 hours</option>
                      <option>24 hours</option>
                    </select>
                  </div>
                </div>
                <div className="settings-field-row">
                  <div className="settings-field">
                    <span className="settings-field-label">Content Language</span>
                    <select className="settings-field-input" value={form.contentLanguage} onChange={(e) => patchForm("contentLanguage", e.target.value)}>
                      <option>English</option>
                      <option>Français</option>
                      <option>Bilingual (EN/FR)</option>
                    </select>
                  </div>
                  <div className="settings-field">
                    <span className="settings-field-label">Drop Visibility</span>
                    <select className="settings-field-input" value={form.dropVisibility} onChange={(e) => patchForm("dropVisibility", e.target.value)}>
                      <option>Global — anyone can view</option>
                      <option>Canada only</option>
                    </select>
                    <span className="settings-field-hint">Fan signups stay global either way</span>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Payouts</div>
                    <div className="settings-card-sub">
                      Stripe Connect · CAD · paid monthly
                      {billingRefreshing ? " · syncing…" : ""}
                    </div>
                  </div>
                </div>
                <div className="settings-pm-card" style={{ marginBottom: 14 }}>
                  <div className="settings-pm-icon" style={{ background: stripe.payoutBank?.verified || stripe.connectPayoutsEnabled ? "var(--st-green)" : "var(--st-border)" }}>
                    {stripe.payoutBank?.verified || stripe.connectPayoutsEnabled ? "✓" : "–"}
                  </div>
                  <div className="settings-pm-body">
                    <div className="settings-pm-title">
                      {stripe.connectAccountId
                        ? (stripe.payoutBank?.verified || stripe.connectPayoutsEnabled
                          ? "Bank account connected"
                          : "Finish Stripe onboarding")
                        : "Connect your bank in Stripe"}
                    </div>
                    <div className="settings-pm-sub">
                      {stripe.payoutBank?.last4
                        ? `${stripe.payoutBank.name || "Bank"} •••• ${stripe.payoutBank.last4}${stripe.payoutBank.verified ? " · verified" : ""}`
                        : stripe.connectAccountId
                          ? "Account linked — add a bank in Stripe Express to receive CAD payouts"
                          : "Complete Stripe Express onboarding to receive payouts"}
                    </div>
                  </div>
                  <button type="button" className="settings-stripe-btn" onClick={() => openCreatorStripeDashboard().catch((e) => showToastMsg(e.message))}>
                    Manage in Stripe ↗
                  </button>
                </div>
                <div className="settings-field-row single">
                  <div className="settings-field">
                    <span className="settings-field-label">Minimum Payout Threshold</span>
                    <select className="settings-field-input" value={form.minimumPayoutThreshold} onChange={(e) => patchForm("minimumPayoutThreshold", e.target.value)}>
                      <option>Pay out any amount</option>
                      <option>$50 minimum</option>
                      <option>$100 minimum</option>
                      <option>$250 minimum</option>
                    </select>
                    <span className="settings-field-hint">Below this, your balance rolls into next month automatically</span>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-head">
                  <div>
                    <div className="settings-card-title">Receipts &amp; Invoices</div>
                    <div className="settings-card-sub">For your own bookkeeping and tax filing</div>
                  </div>
                </div>
                {(stripe.payoutStatements || []).length === 0 ? (
                  <p className="settings-empty-note">Payout statements appear here after your first payout</p>
                ) : (
                  (stripe.payoutStatements || []).map((row) => (
                    <div key={row.id || row.month} className="settings-invoice-row">
                      <div className="settings-invoice-meta">
                        {row.label || `${row.month} payout statement`}
                        <span>Issued {formatBillingDate(row.issuedAt)} · {centsToCad(row.amountCents)}</span>
                      </div>
                      {row.pdfUrl ? (
                        <a href={row.pdfUrl} target="_blank" rel="noopener noreferrer" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }}>Download PDF</a>
                      ) : (
                        <button type="button" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "5px 12px" }} disabled>Download PDF</button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* TAX */}
          {isCreator && (
            <div className={`settings-section-panel ${activeSection === "tax" ? "active" : ""}`}>
              <h1 className="settings-section-title">Tax <em>Information</em></h1>
              <span className="settings-section-hand">required for CRA reporting on your payouts</span>

              <div className="settings-card">
                <div className="settings-collapse-head" onClick={() => setTaxOpen(!taxOpen)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setTaxOpen(!taxOpen)}>
                  <div className="settings-card-title">
                    Tax Details {userProfile?.tax?.onFile && <span className="settings-mini-badge settings-mb-green">On file</span>}
                  </div>
                  <div className={`settings-collapse-chev ${taxOpen ? "open" : ""}`} />
                </div>
                <div className={`settings-collapse-body ${taxOpen ? "open" : ""}`}>
                  <div className="settings-field-row">
                    <div className="settings-field">
                      <span className="settings-field-label">Legal Name</span>
                      <input className="settings-field-input" value={form.taxLegalName} onChange={(e) => patchForm("taxLegalName", e.target.value)} />
                    </div>
                    <div className="settings-field">
                      <span className="settings-field-label">SIN / Business Number</span>
                      <input className="settings-field-input" type="password" value={form.taxSin} onChange={(e) => patchForm("taxSin", e.target.value)} placeholder="••• ••• •••" />
                    </div>
                  </div>
                  <div className="settings-field-row">
                    <div className="settings-field">
                      <span className="settings-field-label">Tax Residency</span>
                      <select className="settings-field-input" value={form.taxResidency} onChange={(e) => patchForm("taxResidency", e.target.value)}>
                        <option>Canada</option>
                      </select>
                    </div>
                    <div className="settings-field">
                      <span className="settings-field-label">Filing Type</span>
                      <select className="settings-field-input" value={form.taxFilingType} onChange={(e) => patchForm("taxFilingType", e.target.value)}>
                        <option>Individual (T4A)</option>
                        <option>Registered Business</option>
                      </select>
                    </div>
                  </div>
                  <span className="settings-field-hint">Stored securely and used only to generate your annual T4A summary. Never shared publicly.</span>
                </div>
              </div>
            </div>
          )}

          {/* SECURITY */}
          <div className={`settings-section-panel ${activeSection === "security" ? "active" : ""}`}>
            <h1 className="settings-section-title">Security</h1>
            <span className="settings-section-hand">keep your account locked down</span>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Password</div></div>
              <div className="settings-field-row">
                <div className="settings-field">
                  <span className="settings-field-label">Current Password</span>
                  <input className="settings-field-input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <div className="settings-field">
                  <span className="settings-field-label">New Password</span>
                  <input className="settings-field-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <button type="button" className="settings-btn settings-btn-primary" disabled={passwordSaving} onClick={handlePasswordUpdate}>
                  {passwordSaving ? "Updating…" : "Update Password"}
                </button>
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Two-factor authentication <span className="settings-mini-badge settings-mb-gold">Recommended</span></div>
                  <div className="settings-tr-desc">Adds a verification step when logging in from a new device</div>
                </div>
                <Toggle on={form.twoFactorEnabled} onClick={() => patchForm("twoFactorEnabled", !form.twoFactorEnabled)} label="Two-factor authentication" />
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Backup recovery codes</div>
                  <div className="settings-tr-desc">Generate one-time codes in case you lose access to your 2FA device</div>
                </div>
                <button type="button" className="settings-btn settings-btn-ghost" style={{ fontSize: 11, padding: "6px 14px" }} disabled={recoveryLoading} onClick={handleGenerateCodes}>
                  {recoveryLoading ? "Generating…" : "Generate Codes"}
                </button>
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info"><div className="settings-tr-title">Email me on new device login</div></div>
                <Toggle on={form.emailOnNewDeviceLogin} onClick={() => patchForm("emailOnNewDeviceLogin", !form.emailOnNewDeviceLogin)} label="Email on new device login" />
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-head">
                <div>
                  <div className="settings-card-title">Login Activity</div>
                  <div className="settings-card-sub">Last 30 days</div>
                </div>
              </div>
              {loginEvents.length === 0 ? (
                <p className="settings-empty-note">No login activity recorded yet</p>
              ) : (
                loginEvents.map((ev) => (
                  <div key={ev.id} className="settings-login-row">
                    <span className={`settings-login-dot ${ev.success ? "settings-ld-ok" : "settings-ld-fail"}`} />
                    <span style={{ flex: 1, color: "var(--st-text-mid)" }}>
                      {ev.success ? "Successful login" : "Failed login attempt"} · {ev.device}{ev.location ? `, ${ev.location}` : ""}
                    </span>
                    <span style={{ color: "var(--st-text-muted)" }}>{formatLoginWhen(ev.timestamp)}</span>
                  </div>
                ))
              )}
            </div>

            <div className="settings-card">
              <div className="settings-card-head">
                <div className="settings-card-title">Active Sessions</div>
              </div>
              <div className="settings-list-row">
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--st-text)" }}>
                    {loginEvents[0]?.device || "This device"}
                    {loginEvents[0]?.location ? ` · ${loginEvents[0].location}` : ""}
                    {" "}
                    <span className="settings-mini-badge settings-mb-green" style={{ marginLeft: 6 }}>This device</span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--st-text-muted)", marginTop: 2 }}>Active now</div>
                </div>
              </div>
              {loginEvents[1] ? (
                <div className="settings-list-row">
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--st-text)" }}>
                      {loginEvents[1].device}{loginEvents[1].location ? ` · ${loginEvents[1].location}` : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--st-text-muted)", marginTop: 2 }}>
                      Last active {formatLoginWhen(loginEvents[1].timestamp)}
                    </div>
                  </div>
                  <button type="button" className="settings-btn settings-btn-danger" style={{ fontSize: 11, padding: "5px 12px" }} disabled>Log out</button>
                </div>
              ) : (
                <p className="settings-empty-note">No other active sessions</p>
              )}
            </div>
          </div>

          {/* DATA & PRIVACY */}
          <div className={`settings-section-panel ${activeSection === "data" ? "active" : ""}`}>
            <h1 className="settings-section-title">Data &amp; <em>Privacy</em></h1>
            <span className="settings-section-hand">your data, your control — PIPEDA compliant</span>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Your Data</div></div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Download my data</div>
                  <div className="settings-tr-desc">A full export of your account, drops history, and transactions — delivered by email within 48 hours</div>
                </div>
                <button
                  type="button"
                  className="settings-btn settings-btn-ghost"
                  disabled={exportLoading}
                  onClick={handleRequestExport}
                >
                  {exportLoading ? "Preparing…" : "Request Export"}
                </button>
              </div>
              <div className="settings-toggle-row">
                <div className="settings-tr-info">
                  <div className="settings-tr-title">Opt-in to anonymized platform research</div>
                  <div className="settings-tr-desc">Helps us improve 100ViewsOnly. Never shared or sold to third parties</div>
                </div>
                <Toggle on={form.researchOptIn} onClick={() => patchForm("researchOptIn", !form.researchOptIn)} label="Research opt-in" />
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Legal</div></div>
              <Link to="/privacy" className="settings-legal-link">Privacy Policy</Link>
              <Link to="/terms" className="settings-legal-link">Terms of Service</Link>
              <Link to="/legal" className="settings-legal-link">Cookie Preferences</Link>
              {consentDate && (
                <div className="settings-stripe-note" style={{ marginTop: 6 }}>
                  <IconClock />
                  You agreed to Terms of Service v{consentVersion} and Privacy Policy v{consentVersion} on {consentDate}
                </div>
              )}
            </div>

            <div className="settings-card">
              <div className="settings-card-head"><div className="settings-card-title">Privacy Contact</div></div>
              <div style={{ fontSize: 12, color: "var(--st-text-mid)", lineHeight: 1.7 }}>
                Questions or complaints about how your data is handled can be sent to <strong>privacy@100viewsonly.com</strong>. We&apos;re a Canadian company based in Parkhill, Ontario, and PIPEDA governs how we handle your information.
              </div>
            </div>
          </div>
        </main>
      </div>

      <div className={`settings-save-bar ${isDirty ? "visible" : ""}`}>
        <span className="settings-sb-text">You have unsaved changes</span>
        <div className="settings-sb-btns">
          <button type="button" className="settings-btn settings-btn-ghost" onClick={handleDiscard}>Discard</button>
          <button type="button" className="settings-btn settings-btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      <div className={`settings-modal-overlay ${usernameModalOpen ? "open" : ""}`} onClick={() => setUsernameModalOpen(false)}>
        <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="settings-modal-icon"><IconLock /></div>
          <div className="settings-modal-title">Change your username</div>
          <div className="settings-modal-desc">
            One-time paid change. After you save a new handle, your old one redirects for 30 days, then stays on a 90-day hold before anyone else can claim it.
          </div>
          <div className="settings-modal-price">$7.99 <span>CAD, charged once</span></div>
          <div className="settings-modal-actions">
            <button type="button" className="settings-btn settings-btn-ghost" onClick={() => setUsernameModalOpen(false)}>Cancel</button>
            <button type="button" className="settings-btn settings-btn-primary" disabled={usernamePayLoading} onClick={handleUsernameCheckout}>
              {usernamePayLoading ? "Redirecting…" : "Continue to Payment"}
            </button>
          </div>
        </div>
      </div>

      <div className={`settings-modal-overlay ${deleteModalOpen ? "open" : ""}`} onClick={() => !deleteLoading && setDeleteModalOpen(false)}>
        <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="settings-modal-icon" style={{ background: "var(--st-red-light, #FDEAEE)", color: "var(--st-red)" }}>
            <IconLock />
          </div>
          <div className="settings-modal-title">Delete your account</div>
          <div className="settings-modal-desc">
            This permanently erases your profile data, cancels active Stripe subscriptions, and removes your login.
            Type <strong>DELETE</strong> to confirm.
          </div>
          <input
            className="settings-field-input"
            style={{ marginBottom: 16 }}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE"
            autoComplete="off"
            disabled={deleteLoading}
          />
          <div className="settings-modal-actions">
            <button type="button" className="settings-btn settings-btn-ghost" disabled={deleteLoading} onClick={() => setDeleteModalOpen(false)}>Cancel</button>
            <button
              type="button"
              className="settings-btn settings-btn-danger"
              disabled={deleteLoading || deleteConfirmText.trim() !== "DELETE"}
              onClick={handleDeleteAccount}
            >
              {deleteLoading ? "Deleting…" : "Delete forever"}
            </button>
          </div>
        </div>
      </div>

      <div className={`settings-modal-overlay ${recoveryCodesOpen ? "open" : ""}`} onClick={() => setRecoveryCodesOpen(false)}>
        <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="settings-modal-icon"><IconLock /></div>
          <div className="settings-modal-title">Backup recovery codes</div>
          <div className="settings-modal-desc">Save these codes somewhere safe. Each code works once. They won&apos;t be shown again.</div>
          <div className="settings-modal-codes">
            {recoveryCodes.map((code) => <div key={code}>{code}</div>)}
          </div>
          <div className="settings-modal-actions">
            <button type="button" className="settings-btn settings-btn-primary" onClick={() => setRecoveryCodesOpen(false)}>I&apos;ve saved these</button>
          </div>
        </div>
      </div>

      {toast && <div className="settings-toast">{toast}</div>}
    </div>
  );
}
